import React, { useState, useMemo, useEffect } from 'react';
import './TimeSheets.css';
import { FaPlus, FaEdit, FaEye, FaChevronDown, FaSearch } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useLocation } from 'react-router-dom';  // added useLocation
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useTimesheet } from './context/TimesheetContext';

const API_BASE_URL = process.env.REACT_APP_EZTS_API_BASE_URL;

//status mapping
const statusMap = {
  1: "draft",
  2: "pending",
  3: "approved",
  4: "rejected",
};

const TimeSheets = () => {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useSelector((state) => state.auth);

  const { 
    startDate, 
    setStartDate, 
    endDate, 
    setEndDate,
    timesheets,
    setTimesheets,
    filteredSheets,
    setFilteredSheets,
    searchInputs,
    setSearchInputs,
    searchVisible,
    setSearchVisible,
    hasFetched,
    setHasFetched
  } = useTimesheet();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'descending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const rowsPerPage = 5;

  // API fetch
  const fetchTimeSheets = async (from = "", to = "") => {
    if (!token || !user) return;
    try {
      setError(null);
      const response = await axios.get(
        `${API_BASE_URL}timesheet/all?userId=${user.userId}&projectId=&from=${from}&to=${to}&orgId=${user.department.costCenter.organization.orgId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const apiData = response.data || [];
      console.log("Timesheet API response:", apiData);

      const mapped = apiData.map(ts => ({
        id: ts.tsId,
        status: statusMap[ts.status] || "unknown",
        fromDate: ts.dateFrom,
        toDate: ts.dateTo,
        approver: ts.approver
          ? `${ts.approver.firstName || ""} ${ts.approver.lastName || ""}`.trim()
          : "-",
        project: ts.project ? ts.project.projectCode : "-",
        standardTime: ts.stSubtotal || 0,
        overtime: ts.otSubtotal || 0,
        total: ts.total || 0,
      }));

      setTimesheets(mapped);
      setFilteredSheets(null);
      setHasFetched(true); // NEW
    } catch (error) {
      setError(
        error.response
          ? `Error ${error.response.status}: ${error.response.data?.message || error.response.statusText}`
          : error.message
      );
      console.error("Error fetching timesheets:", error);
    }
  };

  // used for calling api to get full list without filters
  // useEffect(() => {
  //   fetchTimeSheets();
  // }, [token, user, location.key]); 

  const sortedTimeSheets = useMemo(() => {
    let sortableItems = filteredSheets ? [...filteredSheets] : [...timesheets];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (sortConfig.key === 'approver') {
          aValue = aValue || '';
          bValue = bValue || '';
        }
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [timesheets, filteredSheets, sortConfig]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedTimeSheets.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = sortedTimeSheets.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const requestSort = (key) => {
    let direction = 'descending';
    if (sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = 'ascending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <span className="table-sort-arrows">
          <FaChevronDown className="table-sort-arrow up faded margin-bottom" />
          <FaChevronDown className="table-sort-arrow faded margin-top" />
        </span>
      );
    }
    const isAsc = sortConfig.direction === 'ascending';
    return (
      <FaChevronDown
        className={`table-sort-arrow centered${isAsc ? ' up' : ''}`}
      />
    );
  };

  const handleAdd = () => {
    navigate('/timesheets/new');
  };

  const handleEdit = (id) => {
    navigate(`/timesheets/edit/${id}`);
  };

  const handleView = (id) => {
    navigate(`/timesheets/edit/${id}`);
  };

  // Now applies API call with from/to params
  const handleApplyFilter = () => {
    const from = startDate ? startDate.toISOString().split("T")[0] : "";
    const to = endDate ? endDate.toISOString().split("T")[0] : "";
    fetchTimeSheets(from, to);
  };

  // If navigated back from edit with a refresh request, trigger fetch with provided filters
  useEffect(() => {
    if (location && location.state && location.state.refresh) {
      const from = location.state.filters?.from || (startDate ? startDate.toISOString().split('T')[0] : '');
      const to = location.state.filters?.to || (endDate ? endDate.toISOString().split('T')[0] : '');
      fetchTimeSheets(from, to);
      // clear the navigation state so this only runs once
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, startDate, endDate, token, user]);

  // Search
  const handleSearchIconClick = (key) => {
    setSearchVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSearchInputChange = (key, value) => {
    setSearchInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchInputKeyDown = (key, e) => {
    if (e.key === 'Enter') {
      handleSearch(key);
    } else if (e.key === 'Escape') {
      setSearchVisible((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSearch = (key) => {
    const value = searchInputs[key] || '';
    setFilteredSheets(
      timesheets.filter(sheet =>
        String(sheet[key] || '').toLowerCase().includes(value.toLowerCase())
      )
    );
    setCurrentPage(1);
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  const handleCloseSearch = (key) => {
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  const renderSortableHeader = (key, titleKey) => (
    <th className="table-header sortable">
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span onClick={() => requestSort(key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          {t(`timesheets.${titleKey}`)} {getSortIcon(key)}
        </span>
        <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {searchVisible[key] && (
            <div className="table-search-popover">
              <input
                type="text"
                className="table-search-input"
                value={searchInputs[key] || ''}
                autoFocus
                onChange={e => handleSearchInputChange(key, e.target.value)}
                onKeyDown={e => handleSearchInputKeyDown(key, e)}
                placeholder={t('timesheets.searchPlaceholder', { field: t(`timesheets.${titleKey}`) })}
              />
              <div className="table-search-btn-row">
                <button className="table-search-btn" onClick={() => handleSearch(key)}>
                  <FaSearch style={{ fontSize: '16px' }} />
                </button>
                <button className="table-search-close-btn" onClick={() => handleCloseSearch(key)}>
                  <IoMdClose style={{ fontSize: '18px' }} />
                </button>
              </div>
            </div>
          )}
          <FaSearch
            className="table-search-icon"
            style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '14px' }}
            onClick={() => handleSearchIconClick(key)}
          />
        </span>
      </span>
    </th>
  );

  return (
    <div className="timesheets-container">
      <div className="timesheets-header">
        <h1 className="timesheets-title">{t('timesheets.title')}</h1>
      </div>
      {/* Error display */}
      {error && (
        <div style={{ color: 'red', marginBottom: 16, fontWeight: 500 }}>
          {error}
        </div>
      )}
      <div className="filters-wrapper">
        <div className="filters-container">
          <div className="filter-controls">
            <div className="filter-group">
              <label className="filter-label">{t('timesheets.from')}</label>
              <DatePicker
                selected={startDate}
                onChange={date => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText={t('timesheets.selectStartDate')}
                className="date-input"
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">{t('timesheets.to')}</label>
              <DatePicker
                selected={endDate}
                onChange={date => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText={t('timesheets.selectEndDate')}
                className="date-input"
              />
            </div>
            <button className="apply-filter-button" onClick={handleApplyFilter}>
              {t('timesheets.search')}
            </button>
          </div>
        </div>

        <div className="add-button-container">
          <button className="add-button" onClick={handleAdd}>
            <FaPlus />
            {t('timesheets.add')}
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="timesheets-table">
          <thead>
            <tr>
              {renderSortableHeader('status', 'status')}
              {renderSortableHeader('id', 'id')}
              {renderSortableHeader('fromDate', 'fromDate')}
              {renderSortableHeader('toDate', 'toDate')}
              {renderSortableHeader('approver', 'approver')}
              {renderSortableHeader('project', 'project')}
              {renderSortableHeader('standardTime', 'st')}
              {renderSortableHeader('overtime', 'ot')}
              {renderSortableHeader('total', 'total')}
              <th className="table-header">{t('timesheets.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {!hasFetched ? (
              <tr>
                <td className="table-cell" colSpan={10} style={{ textAlign: 'center' }}>
                  {t('timesheets.applyFilterPrompt')}
                </td>
              </tr>
            ) : currentRows.length === 0 ? (
              <tr>
                <td className="table-cell" colSpan={10} style={{ textAlign: 'center' }}>
                  {t('timesheets.noData', 'No timesheets found.')}
                </td>
              </tr>
            ) : (
              currentRows.map((sheet) => (
                <tr key={sheet.id}>
                  <td className="table-cell">
                    <span className={`status-badge ${sheet.status}`}>
                      {t(`timesheet.${sheet.status}`, sheet.status.charAt(0).toUpperCase() + sheet.status.slice(1))}
                    </span>
                  </td>
                  <td className="table-cell">{sheet.id}</td>
                  <td className="table-cell">{sheet.fromDate}</td>
                  <td className="table-cell">{sheet.toDate}</td>
                  <td className="table-cell"> {sheet.approver}</td>
                    
                  <td className="table-cell">{sheet.project}</td>
                  <td className="table-cell">{sheet.standardTime}</td>
                  <td className="table-cell">{sheet.overtime}</td>
                  <td className="table-cell">{sheet.total}</td>
                  <td className="table-cell">
                    {(sheet.status === 'draft' || sheet.status === 'rejected') && (
                      <button className="action-button edit" onClick={() => handleEdit(sheet.id)}>
                        <FaEdit />
                        {t('timesheets.edit')}
                      </button>
                    )}
                    {(sheet.status === 'pending' || sheet.status === 'approved') && (
                      <button className="action-button edit" onClick={() => handleView(sheet.id)}>
                        <FaEye />
                        {t('timesheets.view')}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <button 
          onClick={handlePreviousPage} 
          disabled={currentPage === 1}
          className="pagination-arrow"
          aria-label={t('timesheets.previous')}
        >
          &lt;
        </button>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={currentPage}
          onChange={e => {
            let page = Number(e.target.value);
            if (page < 1) page = 1;
            if (page > totalPages) page = totalPages;
            setCurrentPage(page);
          }}
          className="pagination-input"
        />
        <span className="pagination-total">
          {t('timesheets.of')} {totalPages}
        </span>
        <button 
          onClick={handleNextPage} 
          disabled={currentPage === totalPages}
          className="pagination-arrow"
          aria-label={t('timesheets.next')}
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default TimeSheets;